import type {
	AnalyticsAggregate,
	BarChartItem,
	RecentActivityItem,
	RouteHealthItem,
} from '../types';

type TimeWindow = '24h' | '48h' | '7d' | '30d';
type Channel = 'Email' | 'Push' | 'Sms' | 'All';

export const AGGREGATE_SEED: Record<
	Channel,
	Record<TimeWindow, AnalyticsAggregate>
> = {
	Email: {
		'24h': { sent: 3821, failed: 214, latency: '204ms', fallback: '1.8%' },
		'48h': { sent: 7200, failed: 390, latency: '202ms', fallback: '1.9%' },
		'7d': { sent: 15430, failed: 876, latency: '218ms', fallback: '2.1%' },
		'30d': { sent: 61200, failed: 3540, latency: '231ms', fallback: '2.4%' },
	},
	Push: {
		'24h': { sent: 6210, failed: 98, latency: '142ms', fallback: '0.9%' },
		'48h': { sent: 12100, failed: 180, latency: '140ms', fallback: '0.9%' },
		'7d': { sent: 41200, failed: 620, latency: '156ms', fallback: '1.1%' },
		'30d': { sent: 162000, failed: 2430, latency: '161ms', fallback: '1.3%' },
	},
	Sms: {
		'24h': { sent: 1540, failed: 132, latency: '312ms', fallback: '3.2%' },
		'48h': { sent: 2900, failed: 245, latency: '310ms', fallback: '3.3%' },
		'7d': { sent: 9820, failed: 780, latency: '328ms', fallback: '3.6%' },
		'30d': { sent: 38700, failed: 3100, latency: '341ms', fallback: '4.0%' },
	},
	All: {
		'24h': { sent: 11571, failed: 444, latency: '219ms', fallback: '2.0%' },
		'48h': { sent: 22200, failed: 815, latency: '217ms', fallback: '2.0%' },
		'7d': { sent: 66450, failed: 2276, latency: '234ms', fallback: '2.3%' },
		'30d': { sent: 261900, failed: 9070, latency: '244ms', fallback: '2.6%' },
	},
};

export const BAR_CHART_SEED: Record<
	Channel,
	Record<TimeWindow, BarChartItem[]>
> = {
	Email: {
		'24h': [
			{ label: 'SendGrid', total: 2100, success: 1940, failed: 160 },
			{ label: 'Mailgun', total: 980, success: 940, failed: 40 },
			{ label: 'AWS SES', total: 120, success: 55, failed: 65 },
		],
		'48h': [
			{ label: 'SendGrid', total: 3900, success: 3620, failed: 280 },
			{ label: 'Mailgun', total: 1820, success: 1760, failed: 60 },
			{ label: 'AWS SES', total: 380, success: 190, failed: 190 },
		],
		'7d': [
			{ label: 'SendGrid', total: 8400, success: 7760, failed: 640 },
			{ label: 'Mailgun', total: 4200, success: 4020, failed: 180 },
			{ label: 'AWS SES', total: 480, success: 220, failed: 260 },
		],
		'30d': [
			{ label: 'SendGrid', total: 33000, success: 30400, failed: 2600 },
			{ label: 'Mailgun', total: 16800, success: 16100, failed: 700 },
			{ label: 'AWS SES', total: 1900, success: 870, failed: 1030 },
		],
	},
	Push: {
		'24h': [
			{ label: 'FCM', total: 4200, success: 4112, failed: 88 },
			{ label: 'APNs', total: 2800, success: 2740, failed: 60 },
			{ label: 'OneSignal', total: 340, success: 334, failed: 6 },
		],
		'48h': [
			{ label: 'FCM', total: 8100, success: 7940, failed: 160 },
			{ label: 'APNs', total: 5400, success: 5290, failed: 110 },
			{ label: 'OneSignal', total: 700, success: 688, failed: 12 },
		],
		'7d': [
			{ label: 'FCM', total: 28100, success: 27480, failed: 620 },
			{ label: 'APNs', total: 18800, success: 18350, failed: 450 },
			{ label: 'OneSignal', total: 2200, success: 2156, failed: 44 },
		],
		'30d': [
			{ label: 'FCM', total: 110000, success: 107500, failed: 2500 },
			{ label: 'APNs', total: 73000, success: 71200, failed: 1800 },
			{ label: 'OneSignal', total: 8600, success: 8430, failed: 170 },
		],
	},
	Sms: {
		'24h': [
			{ label: 'Twilio', total: 980, success: 882, failed: 98 },
			{ label: 'Infobip', total: 320, success: 320, failed: 0 },
			{ label: 'Semaphore', total: 140, success: 137, failed: 3 },
		],
		'48h': [
			{ label: 'Twilio', total: 1850, success: 1670, failed: 180 },
			{ label: 'Infobip', total: 610, success: 610, failed: 0 },
			{ label: 'Semaphore', total: 240, success: 235, failed: 5 },
		],
		'7d': [
			{ label: 'Twilio', total: 6800, success: 6120, failed: 680 },
			{ label: 'Infobip', total: 2100, success: 2100, failed: 0 },
			{ label: 'Semaphore', total: 980, success: 958, failed: 22 },
		],
		'30d': [
			{ label: 'Twilio', total: 26800, success: 24100, failed: 2700 },
			{ label: 'Infobip', total: 8300, success: 8300, failed: 0 },
			{ label: 'Semaphore', total: 3800, success: 3720, failed: 80 },
		],
	},
	All: {
		'24h': [
			{ label: 'SendGrid', total: 2100, success: 1940, failed: 160 },
			{ label: 'FCM', total: 4200, success: 4112, failed: 88 },
			{ label: 'APNs', total: 2800, success: 2740, failed: 60 },
			{ label: 'Twilio', total: 980, success: 882, failed: 98 },
			{ label: 'Mailgun', total: 980, success: 940, failed: 40 },
			{ label: 'Infobip', total: 320, success: 320, failed: 0 },
			{ label: 'AWS SES', total: 120, success: 55, failed: 65 },
		],
		'48h': [
			{ label: 'SendGrid', total: 3900, success: 3620, failed: 280 },
			{ label: 'FCM', total: 8100, success: 7940, failed: 160 },
			{ label: 'APNs', total: 5400, success: 5290, failed: 110 },
			{ label: 'Twilio', total: 1850, success: 1670, failed: 180 },
			{ label: 'Mailgun', total: 1820, success: 1760, failed: 60 },
			{ label: 'Infobip', total: 610, success: 610, failed: 0 },
			{ label: 'AWS SES', total: 380, success: 190, failed: 190 },
		],
		'7d': [
			{ label: 'SendGrid', total: 8400, success: 7760, failed: 640 },
			{ label: 'FCM', total: 28100, success: 27480, failed: 620 },
			{ label: 'APNs', total: 18800, success: 18350, failed: 450 },
			{ label: 'Twilio', total: 6800, success: 6120, failed: 680 },
			{ label: 'Mailgun', total: 4200, success: 4020, failed: 180 },
			{ label: 'Infobip', total: 2100, success: 2100, failed: 0 },
			{ label: 'AWS SES', total: 480, success: 220, failed: 260 },
		],
		'30d': [
			{ label: 'SendGrid', total: 33000, success: 30400, failed: 2600 },
			{ label: 'FCM', total: 110000, success: 107500, failed: 2500 },
			{ label: 'APNs', total: 73000, success: 71200, failed: 1800 },
			{ label: 'Twilio', total: 26800, success: 24100, failed: 2700 },
			{ label: 'Mailgun', total: 16800, success: 16100, failed: 700 },
			{ label: 'Infobip', total: 8300, success: 8300, failed: 0 },
			{ label: 'AWS SES', total: 1900, success: 870, failed: 1030 },
		],
	},
};

export const RECENT_ACTIVITY: RecentActivityItem[] = [
	{
		time: '2m ago',
		event: 'SendGrid',
		action: 'Sent',
		channel: 'Email',
		client: 'smart_app_consumer',
		count: 142,
		status: 'ok',
	},
	{
		time: '4m ago',
		event: 'Twilio',
		action: 'Sent',
		channel: 'SMS',
		client: 'smart_app_merchant',
		count: 38,
		status: 'ok',
	},
	{
		time: '8m ago',
		event: 'AWS SES',
		action: 'Failed',
		channel: 'Email',
		client: 'smart_app_consumer',
		count: 12,
		status: 'err',
	},
	{
		time: '15m ago',
		event: 'FCM',
		action: 'Sent',
		channel: 'Push',
		client: 'smart_app_mobile',
		count: 380,
		status: 'ok',
	},
	{
		time: '22m ago',
		event: 'Mailgun',
		action: 'Sent',
		channel: 'Email',
		client: 'smart_app_consumer',
		count: 74,
		status: 'ok',
	},
	{
		time: '31m ago',
		event: 'APNs',
		action: 'Sent',
		channel: 'Push',
		client: 'smart_app_mobile',
		count: 220,
		status: 'ok',
	},
	{
		time: '44m ago',
		event: 'AWS SES',
		action: 'Error',
		channel: 'Email',
		client: 'smart_app_consumer',
		count: 5,
		status: 'err',
	},
	{
		time: '1h ago',
		event: 'Infobip',
		action: 'Sent',
		channel: 'SMS',
		client: 'smart_app_merchant',
		count: 91,
		status: 'ok',
	},
];

export const ROUTE_HEALTH: RouteHealthItem[] = [
	{
		name: 'Email Fallback Chain',
		subdomain: 'Mail',
		status: 'Active',
		successRate: 94.2,
		avgLatency: '204ms',
		sends7d: 8820,
		lastEvent: '2m ago',
		health: 'warn',
	},
	{
		name: 'Email Weighted Split',
		subdomain: 'Mail',
		status: 'Inactive',
		successRate: 97.1,
		avgLatency: '191ms',
		sends7d: 4200,
		lastEvent: '2d ago',
		health: 'off',
	},
	{
		name: 'Push Weighted Split',
		subdomain: 'Push',
		status: 'Active',
		successRate: 98.8,
		avgLatency: '142ms',
		sends7d: 28100,
		lastEvent: '4m ago',
		health: 'ok',
	},
	{
		name: 'SMS Volume Cap',
		subdomain: 'SMS',
		status: 'Active',
		successRate: 88.3,
		avgLatency: '312ms',
		sends7d: 6800,
		lastEvent: '15m ago',
		health: 'ok',
	},
];
